/// <amd-module name="Wit/WorkItemForm/Attachments/Dialogs/AttachmentPreview" />
import { Button } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { Spinner } from "VSSUI/Spinner";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { ILinkInfo } from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemForm/Resources";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWorkItemTrackingRestClient } from "../../Common/Hooks";
import { IAttachmentActions } from "../AttachmentActions";
import { getAttachmentPreviewType } from "../AttachmentUtils";
import { useAttachmentUri } from "../useAttachmentUri";

export interface IAttachmentPreviewProps extends Omit<IAttachmentActions, "onPreviewAttachment" | "onCopyAttachmentUrl"> {
    attachment: ILinkInfo;
    onDismiss: () => void;
    onNext: () => void;
    onPrevious: () => void;
}

type IPreviewContentProps = Pick<IAttachmentPreviewProps, "attachment">;

export function AttachmentPreview({
    attachment,
    onEditComment,
    onDismiss,
    onDeleteAttachment,
    onDownloadAttachment,
    onNext,
    onPrevious
}: IAttachmentPreviewProps) {
    const backdropRef = useRef<HTMLElement | null>(null);

    // TODO: Ideally we shouldn't need this, but because of our layout, click events don't make it to the actual modal backdrop.
    const onBackdropClick = useCallback(
        (e: React.MouseEvent) => {
            // Only dismiss the modal if the click was on the actual backdrop (rather than a child)
            if (e.target === backdropRef.current) {
                onDismiss();
            }
        },
        [onDismiss]
    );

    const previewContent = useMemo(() => {
        if (!attachment.OriginalName) {
            return null;
        }

        switch (getAttachmentPreviewType(attachment.OriginalName)) {
            case "Text":
                return <TextPreview attachment={attachment} />;
            case "Image":
                return <ImagePreview attachment={attachment} />;
            case "Video":
                return <VideoPreview attachment={attachment} />;
            case "PDF":
                return <PdfPreview attachment={attachment} />;
            default:
                return <PreviewUnavailable onDownloadClick={() => onDownloadAttachment(attachment)} />;
        }
    }, [attachment]);

    return (
        <Callout
            modal
            className="attachment-preview-modal-backdrop"
            onDismiss={onDismiss}
            focuszoneProps={{
                circularNavigation: true,
                focusOnMount: true,
                handleTabKey: true,
                includeDefaults: true
            }}
            escDismiss={true}
        >
            <header className="attachment-preview-header flex-row flex-center justify-space-between">
                <span className="margin-horizontal-20">{attachment.OriginalName}</span>
                <span>
                    {onEditComment && (
                        <Button subtle text={Resources.EditComment} onClick={() => onEditComment(attachment)} iconProps={{ iconName: "Edit" }} />
                    )}
                    <Button
                        subtle
                        text={Resources.DownloadAttachment}
                        onClick={() => onDownloadAttachment(attachment)}
                        iconProps={{ iconName: "Download" }}
                    />
                    {onDeleteAttachment && (
                        <Button subtle text={Resources.Delete} onClick={() => onDeleteAttachment(attachment)} iconProps={{ iconName: "Delete" }} />
                    )}
                    <Button subtle onClick={onDismiss} iconProps={{ iconName: "Clear" }} />
                </span>
            </header>
            <section
                className="attachment-preview-content-area flex-row"
                onClick={onBackdropClick}
                ref={backdrop => (backdropRef.current = backdrop)}
            >
                <Button subtle className="previous-button" iconProps={{ iconName: "ChevronLeft" }} onClick={onPrevious} />
                <div className="attachment-preview-content">{previewContent}</div>
                <Button subtle className="next-button" iconProps={{ iconName: "ChevronRight" }} onClick={onNext} />
            </section>
        </Callout>
    );
}

function TextPreview({ attachment }: IPreviewContentProps) {
    // TODO: https://microsoft.github.io/monaco-editor/

    const witClient = useWorkItemTrackingRestClient();
    const [showLoading, onLoadingComplete] = useLoadingState();
    const [content, setContent] = useState<string | null>(null);

    useEffect(() => {
        witClient.getAttachmentContent(attachment.FilePath!).then(buff => {
            const decoder = new TextDecoder("utf-8");
            const stringContents = decoder.decode(buff);
            setContent(stringContents);
            onLoadingComplete();
        });
    }, [attachment]);

    const lineCount = content?.split("\n").length ?? 0;
    const lineNumbers = Array.from({ length: lineCount }).map((_, i) => i + 1);

    return (
        <div className="text-attachment-preview flex-row">
            <span className="line-numbers">
                {lineNumbers.map(lineNumber => (
                    <div key={`line-number-${lineNumber}`}>{lineNumber}</div>
                ))}
            </span>
            <pre>
                <code>{content}</code>
            </pre>
            {showLoading && <Spinner />}
        </div>
    );
}

function ImagePreview({ attachment }: IPreviewContentProps) {
    const [showLoading, onLoadingComplete] = useLoadingState();
    const src = useAttachmentUri(attachment);

    return (
        <>
            {src && <img src={src} className="image-attachment-preview" onLoad={onLoadingComplete} />}
            {showLoading && <Spinner />}
        </>
    );
}

function VideoPreview({ attachment }: IPreviewContentProps) {
    const [showLoading, onLoadingComplete] = useLoadingState();
    const src = useAttachmentUri(attachment, { download: false, fileName: attachment.OriginalName, "api-version": "5.0-preview.2" });

    return (
        <>
            <video className="attachment-preview-media" controls onCanPlay={onLoadingComplete} src={src}>
                {Resources.HTMLVideoSupport}
            </video>
            {showLoading && <Spinner />}
        </>
    );
}

function PdfPreview({ attachment }: IPreviewContentProps) {
    const [showLoading, onLoadingComplete] = useLoadingState();
    const src = useAttachmentUri(attachment);

    const dataUrl = `${src}?fileName=${
        attachment.OriginalName !== undefined && encodeURIComponent(attachment.OriginalName)
    }&download=false#toolbar=0`;
    return (
        <>
            <object data={dataUrl} className="attachment-preview-window-content" type="application/pdf" onLoad={onLoadingComplete}>
                <iframe src={dataUrl} className="attachment-preview-window-content" onLoad={onLoadingComplete} />
            </object>
            {showLoading && <Spinner />}
        </>
    );
}

function PreviewUnavailable({ onDownloadClick }: { onDownloadClick: () => void }) {
    return (
        <div className="attachment-preview-unavailable">
            <ZeroData
                iconProps={{ iconName: "FileBug" }}
                imageAltText={Resources.AttachmentNoPreview}
                primaryText={Resources.AttachmentNoPreview}
                actionText={Resources.DownloadAttachment}
                actionType={ZeroDataActionType.ctaButton}
                onActionClick={onDownloadClick}
            />
        </div>
    );
}

function useLoadingState(): [boolean, () => void] {
    const [loading, setLoading] = useState(true);

    const [isWithinGracePeriod, setIsWithinGracePeriod] = useState(true);
    useEffect(() => {
        setTimeout(() => setIsWithinGracePeriod(false), 200);
    }, []);

    const showLoading = loading && !isWithinGracePeriod;
    const onLoadingComplete = useCallback(() => setLoading(false), []);

    return [showLoading, onLoadingComplete];
}
